import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Badge, Spinner, Alert, Table } from 'react-bootstrap';
import { FaCalendarAlt, FaTrash, FaCheck, FaTimes, FaEdit } from 'react-icons/fa';
import { addFestival, updateFestival, deleteFestival } from '../../../services/firebase/festivalService';
import { useAuth } from '../../../context/AuthContext';
import moment from 'moment';

const QUICK_FESTIVALS = [
  'Diwali',
  'Ganesh Chaturthi',
  'Holi / Dhulivandan',
  'Gudhi Padwa',
  'Dussehra',
  'Eid-ul-Fitr',
  'Christmas',
  'Makar Sankranti',
  'Shiv Jayanti',
  'Independence Day',
  'Republic Day',
  'Raksha Bandhan'
];

const AddFestivalModal = ({
  show,
  onClose,
  initialDate,
  festivals = [],
  canManageFestivals,
  onFestivalsChanged
}) => {
  const { user } = useAuth();
  const userRole = (user?.role || '').toLowerCase().trim();
  const userDesignation = (user?.designation || user?.job?.designation || '').toLowerCase().trim();
  const hasAccess = canManageFestivals !== undefined 
    ? canManageFestivals 
    : (['admin', 'hr', 'ceo', 'founder', 'superadmin'].includes(userRole) || 
       userDesignation.includes('ceo') || 
       userDesignation.includes('founder'));

  const [date, setDate] = useState(initialDate || moment().format('YYYY-MM-DD'));
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [editingFestival, setEditingFestival] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (initialDate) {
      setDate(initialDate);
      const existing = (festivals || []).find(f => moment(f.date).format('YYYY-MM-DD') === moment(initialDate).format('YYYY-MM-DD'));
      if (existing) {
        setEditingFestival(existing);
        setName(existing.name || '');
        setDescription(existing.description || '');
      } else {
        setEditingFestival(null);
        setName('');
        setDescription('');
      }
    } else {
      setDate(moment().format('YYYY-MM-DD'));
      setEditingFestival(null);
      setName('');
      setDescription('');
    }
    setError('');
    setSuccess('');
  }, [initialDate, show, festivals]);

  const handleStartEdit = (fest) => {
    setEditingFestival(fest);
    setDate(fest.date);
    setName(fest.name);
    setDescription(fest.description || '');
    setError('');
    setSuccess('');
  };

  const handleCancelEdit = () => {
    setEditingFestival(null);
    setName('');
    setDescription('');
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasAccess) {
      setError('Access Denied: Only Admin, HR, CEO, and Founder are permitted to manage festivals.');
      return;
    }
    if (!date) {
      setError('Please select a date.');
      return;
    }
    if (!name.trim()) {
      setError('Please enter a festival name.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (editingFestival) {
        await updateFestival(editingFestival.id, {
          date,
          name: name.trim(),
          description: description.trim(),
          updatedBy: user?.name || user?.email || 'admin'
        });
        setSuccess(`🎉 Festival "${name.trim()}" updated successfully!`);
        setEditingFestival(null);
      } else {
        await addFestival({
          date,
          name: name.trim(),
          description: description.trim(),
          createdBy: user?.name || user?.email || 'admin'
        });
        setSuccess(`🎉 Festival "${name.trim()}" added successfully with Rainbow Color!`);
      }
      setName('');
      setDescription('');
      if (onFestivalsChanged) {
        onFestivalsChanged();
      }
    } catch (err) {
      console.error('Failed to save festival:', err);
      setError('Failed to save festival: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fest) => {
    if (!hasAccess) {
      setError('Access Denied: Only Admin, HR, CEO, and Founder are permitted to delete festivals.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete festival "${fest.name}" on ${fest.date}?`)) {
      return;
    }
    setDeletingId(fest.id || fest.date);
    try {
      await deleteFestival(fest.id, fest.date);
      setSuccess(`Festival "${fest.name}" removed.`);
      if (editingFestival && (editingFestival.id === fest.id || editingFestival.date === fest.date)) {
        handleCancelEdit();
      }
      if (onFestivalsChanged) {
        onFestivalsChanged();
      }
    } catch (err) {
      console.error('Failed to delete festival:', err);
      setError('Failed to delete festival.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton style={{
        background: 'linear-gradient(90deg, #ff3838 0%, #ff9f1a 20%, #f1c40f 40%, #2ecc71 60%, #00c0ef 80%, #8e44ad 100%)',
        color: 'white'
      }}>
        <Modal.Title className="fw-bold d-flex align-items-center" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
          <span className="me-2" style={{ fontSize: '1.4rem' }}>🎉</span>
          Manage Festivals & Rainbow Days
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4 bg-light">
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        {!hasAccess && (
          <Alert variant="info" className="rounded-3 shadow-sm border-0 d-flex align-items-center">
            <span className="me-2 fs-5">🔒</span>
            <div>
              <strong>Restricted Access:</strong> Only <strong>Admin</strong>, <strong>HR</strong>, <strong>CEO</strong>, and <strong>Founder</strong> have permission to add, edit, or delete festivals.
            </div>
          </Alert>
        )}

        {/* Add/Edit Festival Card - Only visible to Admin, HR, CEO, and Founder */}
        {hasAccess && (
          <div className="bg-white p-4 rounded-4 shadow-sm mb-4 border">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <h5 className="fw-bold mb-0 text-dark">
                  {editingFestival ? '✏️ Edit Festival' : 'Add New Festival'}
                </h5>
                <Badge className="ms-2 festival-badge-rainbow">
                  🌈 Rainbow Color Preview
                </Badge>
              </div>
              {editingFestival && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="rounded-pill px-3"
                  onClick={handleCancelEdit}
                >
                  <FaTimes className="me-1" /> Cancel Edit
                </Button>
              )}
            </div>

          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">
                    <FaCalendarAlt className="me-1 text-primary" /> Festival Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    className="border-primary border-opacity-25"
                  />
                </Form.Group>
              </Col>

              <Col md={7}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">
                    🎉 Festival Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Diwali, Ganesh Chaturthi, Holi..."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>

              {/* Quick Suggestion Chips */}
              <Col xs={12}>
                <div className="small fw-bold text-muted mb-1">Quick Select Popular Festivals:</div>
                <div className="d-flex flex-wrap gap-1">
                  {QUICK_FESTIVALS.map((fest, idx) => (
                    <Badge
                      key={idx}
                      bg="light"
                      text="dark"
                      className="border px-2 py-1 cursor-pointer"
                      style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                      onClick={() => setName(fest)}
                      role="button"
                    >
                      + {fest}
                    </Badge>
                  ))}
                </div>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">Description / Notes (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Public Festival / Office Holiday"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* Rainbow Live Preview Box */}
              <Col xs={12}>
                <div className="p-3 rounded-3" style={{
                  background: 'linear-gradient(135deg, rgba(255, 56, 56, 0.15) 0%, rgba(255, 159, 26, 0.15) 18%, rgba(255, 250, 101, 0.22) 36%, rgba(50, 255, 126, 0.16) 54%, rgba(24, 220, 255, 0.16) 72%, rgba(125, 95, 255, 0.2) 100%)',
                  border: '2px dashed #ff9f1a'
                }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <span className="small fw-bold text-muted d-block">Live Rainbow Calendar Preview:</span>
                      <strong className="fs-6 me-2">Date: {date ? moment(date).format('DD MMM YYYY (dddd)') : '-'}</strong>
                    </div>
                    <div>
                      <span className="festival-badge-rainbow px-3 py-1 fs-6">
                        🎉 {name || 'Festival Name'}
                      </span>
                    </div>
                  </div>
                </div>
              </Col>

              <Col xs={12} className="text-end d-flex justify-content-end gap-2">
                {editingFestival && (
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancelEdit}
                    className="rounded-pill px-4"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: 'linear-gradient(90deg, #ff3838, #ff9f1a, #2ecc71, #00c0ef, #8e44ad)',
                    border: 'none',
                    fontWeight: '700',
                    padding: '8px 24px',
                    borderRadius: '50px',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
                  }}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FaCheck className="me-2" />
                  )}
                  {editingFestival ? 'Update Festival' : 'Save Festival'}
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
        )}

        {/* Existing Festivals List */}
        <div className="bg-white p-4 rounded-4 shadow-sm border">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0 text-dark">
              📅 Added Festivals ({festivals.length})
            </h6>
            <small className="text-muted">These dates display rainbow color in the calendar</small>
          </div>

          {festivals.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <span style={{ fontSize: '2rem' }}>🌈</span>
              <p className="mt-2 mb-0">No festivals added yet. Add a festival above to display rainbow color on that date!</p>
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: '250px' }}>
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="small">Date</th>
                    <th className="small">Festival Name</th>
                    <th className="small">Description</th>
                    {hasAccess && <th className="small text-center">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {festivals.map((fest, idx) => (
                    <tr key={fest.id || idx}>
                      <td className="fw-bold font-monospace">
                        {moment(fest.date).format('DD-MMM-YYYY')}
                        <small className="d-block text-muted fw-normal">{moment(fest.date).format('dddd')}</small>
                      </td>
                      <td>
                        <span className="festival-badge-rainbow">
                          🎉 {fest.name}
                        </span>
                      </td>
                      <td className="small text-muted">
                        {fest.description || '-'}
                      </td>
                      {hasAccess && (
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="rounded-circle p-1"
                              style={{ width: '30px', height: '30px' }}
                              onClick={() => handleStartEdit(fest)}
                              title="Edit Festival"
                            >
                              <FaEdit size={12} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="rounded-circle p-1"
                              style={{ width: '30px', height: '30px' }}
                              disabled={deletingId === (fest.id || fest.date)}
                              onClick={() => handleDelete(fest)}
                              title="Delete Festival"
                            >
                              {deletingId === (fest.id || fest.date) ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FaTrash size={12} />
                              )}
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 bg-light">
        <Button variant="secondary" onClick={onClose} className="rounded-pill px-4">
          <FaTimes className="me-1" /> Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { AddFestivalModal };
export default AddFestivalModal;
